import { Flex } from 'antd';
import { Dayjs } from 'dayjs';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getDayjs, getDayjsNow, ISODate } from '../utils';
import { CalendarHeader } from './CalendarHeader';
import { MonthCalendar } from './MonthCalendar';
import {
  Holiday,
  MonthIndex,
  MONTH_VIEW,
  ViewType,
  CalendarCommonProps,
} from './types';
import { YearCalendar } from './YearCalendar';

export interface CalendarProps {
  holidays: Holiday[];
  onSelect: CalendarCommonProps['setSelected'];
  value: Set<string>;
  view?: ViewType;
  id: string;
}

const toggle = (
  dateStr: ISODate,
  setSelected: CalendarCommonProps['setSelected']
) => {
  setSelected((state) => {
    const newState = new Set(state);
    if (state.has(dateStr)) {
      newState.delete(dateStr);
    } else {
      newState.add(dateStr);
    }

    return newState;
  });
};

function useCalendar(props: CalendarProps) {
  const { value, view } = props;
  const [type, setType] = useState(view ?? MONTH_VIEW);
  const [period, setPeriod] = useState(() => {
    if (!value.size) {
      return getDayjsNow();
    }

    const dates = [...value];
    dates.sort((a, b) => a.localeCompare(b));
    return getDayjs(dates.at(-1)) as Dayjs;
  });

  useEffect(() => {
    if (view) {
      setType(view);
    }
  }, [view]);
  return { period, setPeriod, setType, type };
}

export function Calendar(props: CalendarProps) {
  const { onSelect, value, id } = props;
  const { period, setPeriod, type, setType } = useCalendar(props);
  const onToggle = useCallback(
    (day: Dayjs, dateStr: ISODate) => toggle(dateStr, onSelect),
    [onSelect]
  );

  return (
    <Flex vertical>
      <CalendarHeader
        period={period}
        onChangePeriod={setPeriod}
        onChangeType={setType}
        type={type}
        id={id}
      />
      <BodyWrapper>
        {type === MONTH_VIEW ? (
          <MonthCalendar
            holidays={props.holidays}
            monthIndex={period.month() as MonthIndex}
            onToggle={onToggle}
            selected={value}
            year={period.year()}
          />
        ) : (
          <YearCalendar
            holidays={props.holidays}
            onToggle={onToggle}
            selected={value}
            year={period.year()}
          />
        )}
      </BodyWrapper>
    </Flex>
  );
}

const BodyWrapper = styled.div`
  margin: 0 44px;
`;
